'use client';
import { useEffect, useMemo } from 'react';
import Select from '@/components/ui/Select';
import { useLanguage } from '@/context/LanguageContext';
import { getYesNoOptions } from '@/lib/calculatorI18n';
import styles from '../Step1BasicData.module.css';

export default function PropertyUsageFields({
  formData,
  setFormData,
  validationErrors,
  showValidationErrors,
  setValidationErrors,
}) {
  const { t } = useLanguage();
  const yesNoOptions = useMemo(() => getYesNoOptions(t), [t]);

  const residenceTypeOptions = useMemo(
    () => [
      {
        value: 'PERMANENT',
        label: t('calculator.forms.nt_residence_permanent'),
      },
      {
        value: 'NON_PERMANENT',
        label: t('calculator.forms.nt_residence_non_permanent'),
      },
    ],
    [t]
  );

  const rentalTypeOptions = useMemo(
    () => [
      { value: 'NONE', label: t('calculator.forms.nt_rental_none') },
      { value: 'LONG_TERM', label: t('calculator.forms.nt_rental_long') },
      { value: 'SHORT_TERM', label: t('calculator.forms.nt_rental_short') },
    ],
    [t]
  );

  const insuranceVariantOptions = useMemo(
    () => [
      {
        value: 'BASIC',
        label: t('calculator.forms.nt_variant_basic'),
      },
      {
        value: 'EXTENDED',
        label: t('calculator.forms.nt_variant_extended'),
      },
    ],
    [t]
  );

  const handleBooleanChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear validation error
    if (value !== undefined && validationErrors[field] && setValidationErrors) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSelectChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear validation error
    if (value && validationErrors[field] && setValidationErrors) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  useEffect(() => {
    if (formData.rental_type) return;
    setFormData(prev => ({ ...prev, rental_type: 'NONE' }));
  }, [formData.rental_type, setFormData]);

  return (
    <div className={styles.propertyUsageGrid}>
      {/* Residence Type */}
      <div className={styles.formGroup}>
        <label className={styles.label}>
          {t('calculator.forms.nt_residence_label')}
        </label>
        <Select
          options={residenceTypeOptions}
          value={formData.residence_type || ''}
          onChange={val => handleSelectChange('residence_type', val)}
          placeholder={t('calculator.forms.placeholder_select')}
          className={validationErrors.residence_type ? styles.selectError : ''}
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.residence_type ? styles.errorHighlight : ''}`}
        >
          {validationErrors.residence_type || ''}
        </div>
      </div>

      {/* Rental Type */}
      <div className={styles.formGroup}>
        <label className={styles.label}>{t('calculator.forms.nt_rental_label')}</label>
        <Select
          options={rentalTypeOptions}
          value={formData.rental_type || 'NONE'}
          onChange={val => handleSelectChange('rental_type', val)}
          placeholder={t('calculator.forms.placeholder_select')}
          className={validationErrors.rental_type ? styles.selectError : ''}
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.rental_type ? styles.errorHighlight : ''}`}
        >
          {validationErrors.rental_type || ''}
        </div>
      </div>

      {/* Insurance Variant */}
      <div className={styles.formGroup}>
        <label className={styles.label}>{t('calculator.forms.nt_variant_label')}</label>
        <Select
          options={insuranceVariantOptions}
          value={formData.insurance_variant || ''}
          onChange={val => handleSelectChange('insurance_variant', val)}
          placeholder={t('calculator.forms.placeholder_select_variant')}
          className={
            validationErrors.insurance_variant ? styles.selectError : ''
          }
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.insurance_variant ? styles.errorHighlight : ''}`}
        >
          {validationErrors.insurance_variant || ''}
        </div>
      </div>

      {/* Yes/No Questions */}
      <div className={styles.formGroup}>
        <label className={styles.label}>
          {t('calculator.forms.nt_interior_assets')}
        </label>
        <div className={styles.radioGroup}>
          {yesNoOptions.map(opt => (
            <label
              key={String(opt.value)}
              className={`${styles.radioOption} ${formData.is_interior_property_relevant === opt.value ? styles.radioSelected : ''}`}
            >
              <input
                type='radio'
                name='is_interior_property_relevant'
                value={String(opt.value)}
                checked={formData.is_interior_property_relevant === opt.value}
                onChange={() =>
                  handleBooleanChange(
                    'is_interior_property_relevant',
                    opt.value
                  )
                }
                className={styles.radioInput}
              />
              <span className={styles.radioLabel}>{opt.label}</span>
            </label>
          ))}
        </div>
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.is_interior_property_relevant ? styles.errorHighlight : ''}`}
        >
          {validationErrors.is_interior_property_relevant || ''}
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>
          {t('calculator.forms.nt_internal_damages')}
        </label>
        <div className={styles.radioGroup}>
          {yesNoOptions.map(opt => (
            <label
              key={String(opt.value)}
              className={`${styles.radioOption} ${formData.is_internal_damages_needed === opt.value ? styles.radioSelected : ''}`}
            >
              <input
                type='radio'
                name='is_internal_damages_needed'
                value={String(opt.value)}
                checked={formData.is_internal_damages_needed === opt.value}
                onChange={() =>
                  handleBooleanChange('is_internal_damages_needed', opt.value)
                }
                className={styles.radioInput}
              />
              <span className={styles.radioLabel}>{opt.label}</span>
            </label>
          ))}
        </div>
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.is_internal_damages_needed ? styles.errorHighlight : ''}`}
        >
          {validationErrors.is_internal_damages_needed || ''}
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>{t('calculator.forms.nt_smart_devices')}</label>
        <div className={styles.radioGroup}>
          {yesNoOptions.map(opt => (
            <label
              key={String(opt.value)}
              className={`${styles.radioOption} ${formData.is_smart_devices_relevant === opt.value ? styles.radioSelected : ''}`}
            >
              <input
                type='radio'
                name='is_smart_devices_relevant'
                value={String(opt.value)}
                checked={formData.is_smart_devices_relevant === opt.value}
                onChange={() =>
                  handleBooleanChange('is_smart_devices_relevant', opt.value)
                }
                className={styles.radioInput}
              />
              <span className={styles.radioLabel}>{opt.label}</span>
            </label>
          ))}
        </div>
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.is_smart_devices_relevant ? styles.errorHighlight : ''}`}
        >
          {validationErrors.is_smart_devices_relevant || ''}
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>
          {t('calculator.forms.nt_civil_liability')}
        </label>
        <div className={styles.radioGroup}>
          {yesNoOptions.map(opt => (
            <label
              key={String(opt.value)}
              className={`${styles.radioOption} ${formData.is_civil_liability_relevant === opt.value ? styles.radioSelected : ''}`}
            >
              <input
                type='radio'
                name='is_civil_liability_relevant'
                value={String(opt.value)}
                checked={formData.is_civil_liability_relevant === opt.value}
                onChange={() =>
                  handleBooleanChange('is_civil_liability_relevant', opt.value)
                }
                className={styles.radioInput}
              />
              <span className={styles.radioLabel}>{opt.label}</span>
            </label>
          ))}
        </div>
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.is_civil_liability_relevant ? styles.errorHighlight : ''}`}
        >
          {validationErrors.is_civil_liability_relevant || ''}
        </div>
      </div>
    </div>
  );
}
